import { useState, useEffect, useLayoutEffect, useRef, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type UserListItem } from '../api/client';
import { fullName } from '../utils/display';

export function AdminUsersPage() {
  return (
    <>
      <h2 className="text-lg font-semibold text-slate-900">User management</h2>
      <UsersTable />
    </>
  );
}

function UsersTable() {
  const qc = useQueryClient();
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers().then(r => r.users),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<UserListItem | undefined>();
  const [resettingFor, setResettingFor] = useState<UserListItem | undefined>();
  const [resetResult, setResetResult] = useState<{ user: UserListItem; tempPassword: string } | undefined>();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Add, remove, edit, or change roles. New users receive a Cognito invite email with a temporary password.
        </p>
        <button
          onClick={() => setShowAdd(v => !v)}
          className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark"
        >
          {showAdd ? 'Cancel' : '+ Add user'}
        </button>
      </div>

      {showAdd && <AddUserForm onSuccess={() => { setShowAdd(false); invalidate(); }} />}

      {usersQuery.isLoading && <p className="text-sm text-slate-500">Loading users…</p>}
      {usersQuery.error && <ErrorBanner error={usersQuery.error} />}

      {usersQuery.data && (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">User ID</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usersQuery.data.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">No users yet.</td></tr>
              )}
              {usersQuery.data.map(u => (
                <UserRow
                  key={u.userId}
                  user={u}
                  onEdit={() => setEditing(u)}
                  onResetPassword={() => setResettingFor(u)}
                  onChanged={invalidate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); invalidate(); }}
        />
      )}

      {resettingFor && !resetResult && (
        <ResetPasswordConfirm
          user={resettingFor}
          onCancel={() => setResettingFor(undefined)}
          onDone={(tempPassword) => {
            setResetResult({ user: resettingFor, tempPassword });
            setResettingFor(undefined);
          }}
        />
      )}

      {resetResult && (
        <ResetPasswordResult
          email={resetResult.user.email}
          tempPassword={resetResult.tempPassword}
          onClose={() => setResetResult(undefined)}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  onEdit,
  onResetPassword,
  onChanged,
}: {
  user: UserListItem;
  onEdit: () => void;
  onResetPassword: () => void;
  onChanged: () => void;
}) {
  const updateRole = useMutation({
    mutationFn: () => api.updateUser(user.userId, { role: user.role === 'admin' ? 'agent' : 'admin' }),
    onSuccess: onChanged,
  });
  const deleteUser = useMutation({
    mutationFn: () => api.deleteUser(user.userId),
    onSuccess: onChanged,
  });
  const clearSession = useMutation({
    mutationFn: () => api.clearUserSession(user.userId),
    onSuccess: onChanged,
  });

  function confirmDelete() {
    if (window.confirm(`Delete ${user.email}? This is permanent.`)) deleteUser.mutate();
  }

  function confirmClearSession() {
    if (
      window.confirm(
        `Clear ${user.email}'s session? They'll be signed out on every device within ~30 seconds and will need to sign in again.`,
      )
    ) {
      clearSession.mutate();
    }
  }

  const name = fullName(user);
  const busy = updateRole.isPending || deleteUser.isPending || clearSession.isPending;

  return (
    <tr>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">
        {user.lumiUserId ?? '—'}
      </td>
      <td className="px-4 py-3 text-slate-900">
        {name && name !== user.email ? name : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 font-medium text-slate-900">{user.email}</td>
      <td className="px-4 py-3">
        <span className={user.role === 'admin' ? 'text-brand-dark font-medium' : 'text-slate-600'}>
          {user.role === 'admin' ? 'Administrator' : 'Agent'}
        </span>
      </td>
      <td className="px-4 py-3 text-slate-600">{user.status}</td>
      <td className="px-4 py-3 text-right">
        <ActionsMenu
          busy={busy}
          items={[
            { label: 'Edit', onClick: onEdit },
            { label: 'Reset password', onClick: onResetPassword },
            { label: 'Clear session', onClick: confirmClearSession },
            {
              label: `Make ${user.role === 'admin' ? 'agent' : 'admin'}`,
              onClick: () => updateRole.mutate(),
            },
            { label: 'Delete', onClick: confirmDelete, destructive: true },
          ]}
        />
      </td>
    </tr>
  );
}

function AddUserForm({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'admin' | 'agent'>('agent');
  const [error, setError] = useState<string | undefined>();

  const create = useMutation({
    mutationFn: () => api.createUser({
      email,
      role,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
    }),
    onSuccess: () => { setEmail(''); setFirstName(''); setLastName(''); onSuccess(); },
    onError: (err: unknown) => setError(toMessage(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    create.mutate();
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-700">First name</span>
          <input
            type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Last name</span>
          <input
            type="text" value={lastName} onChange={e => setLastName(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-700">Email</span>
          <input
            type="email" required value={email} onChange={e => setEmail(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Role</span>
          <select
            value={role} onChange={e => setRole(e.target.value as 'admin' | 'agent')}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value="agent">Agent</option>
            <option value="admin">Administrator</option>
          </select>
        </label>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <button
        type="submit" disabled={create.isPending}
        className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
      >
        {create.isPending ? 'Creating…' : 'Create user & send invite'}
      </button>
    </form>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [email, setEmail] = useState(user.email);
  const [error, setError] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: () => {
      const patch: { firstName?: string; lastName?: string; email?: string } = {};
      const f = firstName.trim();
      const l = lastName.trim();
      const e = email.trim().toLowerCase();
      if (f !== (user.firstName ?? '').trim()) patch.firstName = f;
      if (l !== (user.lastName ?? '').trim()) patch.lastName = l;
      if (e !== user.email) patch.email = e;
      if (Object.keys(patch).length === 0) return Promise.resolve({ ok: true as const });
      return api.updateUser(user.userId, patch);
    },
    onSuccess: onSaved,
    onError: (err: unknown) => setError(toMessage(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    save.mutate();
  }

  return (
    <Modal onClose={onClose} title={`Edit ${user.email}`}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-700">First name</span>
            <input
              type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-700">Last name</span>
            <input
              type="text" value={lastName} onChange={e => setLastName(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-slate-700">Email</span>
          <input
            type="email" required value={email} onChange={e => setEmail(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <span className="text-[11px] text-slate-500 mt-1 block">
            Changing the email signs the user out of any active sessions.
          </span>
        </label>
        {error && <p className="text-xs text-red-700">{error}</p>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="submit" disabled={save.isPending}
            className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordConfirm({
  user,
  onCancel,
  onDone,
}: {
  user: UserListItem;
  onCancel: () => void;
  onDone: (tempPassword: string) => void;
}) {
  const [error, setError] = useState<string | undefined>();
  const reset = useMutation({
    mutationFn: () => api.resetUserPassword(user.userId),
    onSuccess: (r) => onDone(r.tempPassword),
    onError: (err: unknown) => setError(toMessage(err)),
  });

  return (
    <Modal onClose={onCancel} title={`Reset password for ${user.email}`}>
      <div className="space-y-3 text-sm text-slate-700">
        <p>
          We'll generate a new temporary password and force the user to choose
          a new one on their next sign-in. Any active sessions will be signed out.
        </p>
        <p className="text-xs text-slate-500">
          The temporary password is shown once on the next screen — copy it
          and share it with the user out-of-band (chat, phone, etc.).
        </p>
        {error && <p className="text-xs text-red-700">{error}</p>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { setError(undefined); reset.mutate(); }}
            disabled={reset.isPending}
            className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
          >
            {reset.isPending ? 'Generating…' : 'Reset password'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResetPasswordResult({
  email,
  tempPassword,
  onClose,
}: {
  email: string;
  tempPassword: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(tempPassword).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => undefined,
    );
  }

  return (
    <Modal onClose={onClose} title="Temporary password generated">
      <div className="space-y-3 text-sm text-slate-700">
        <p>Share this with <span className="font-medium">{email}</span> out-of-band:</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-sm bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 select-all">
            {tempPassword}
          </code>
          <button
            type="button" onClick={copy}
            className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium hover:bg-slate-50"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          This is the only time we'll show this password. The user will be
          forced to choose a new one on their next sign-in.
        </p>
        <div className="flex items-center justify-end pt-2">
          <button
            type="button" onClick={onClose}
            className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface ActionItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

/**
 * Row-level kebab menu. Opens on click, closes on outside-click, Escape,
 * scroll, or window resize.
 *
 * The menu panel is rendered via a React portal to `document.body` with
 * `position: fixed` coordinates computed from the trigger button's
 * bounding rect — this avoids the parent table wrapper's
 * `overflow-hidden` clipping the dropdown for rows near the bottom of
 * the page.
 *
 * If the menu would overflow below the viewport (i.e. the row is near
 * the bottom), it auto-flips and opens upward instead.
 */
function ActionsMenu({ items, busy }: { items: ActionItem[]; busy?: boolean }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; right: number }>({ right: 0 });

  // Estimate menu height to decide flip direction. ~32px per item + 8px padding.
  const ESTIMATED_MENU_HEIGHT = items.length * 32 + 8;
  const GAP = 4;

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < ESTIMATED_MENU_HEIGHT + 16 && rect.top > ESTIMATED_MENU_HEIGHT;
    if (flipUp) {
      setCoords({
        bottom: window.innerHeight - rect.top + GAP,
        right: window.innerWidth - rect.right,
      });
    } else {
      setCoords({
        top: rect.bottom + GAP,
        right: window.innerWidth - rect.right,
      });
    }
  }, [open, ESTIMATED_MENU_HEIGHT]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    // Close on scroll/resize — the absolute coordinates would drift
    // otherwise and re-anchoring on every scroll feels jittery.
    function onScrollOrResize() { setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
      >
        {busy ? '…' : <span aria-hidden className="text-lg leading-none">⋯</span>}
        <span className="sr-only">Open actions menu</span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: coords.top,
            bottom: coords.bottom,
            right: coords.right,
          }}
          className="z-50 w-44 rounded-lg bg-white shadow-lg border border-slate-200 py-1"
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={
                'block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ' +
                (item.destructive ? 'text-red-600 hover:text-red-800' : 'text-slate-700 hover:text-slate-900')
              }
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

function Modal({ children, title, onClose }: { children: React.ReactNode; title: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }) {
  return <p className="text-sm text-red-700">{toMessage(error)}</p>;
}

function toMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined;
    if (body?.error === 'email_already_exists') return 'A user with that email already exists.';
    if (body?.error === 'user_not_found') return 'User not found (maybe already deleted).';
    return body?.error ?? `API error ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}
